/**
 * The feature rollout panel — one surface replacing the two side-by-side lists
 * on `/sa/features/:id` (LOCKER-408 DoD3-DoD6).
 *
 * It replaces `FeatureTenantsList` (organizations, with a checkbox that had no
 * `onChange`) and `FeatureAssignments` (a read-only mirror of the same data,
 * filtered separately). Two lists over one dataset is why the page could
 * disagree with itself: the left list resolved a row from the assignment alone
 * and the right one listed only rows that had assignments, so neither showed
 * the feature default that was actually granting access.
 *
 * Bulk scope is an explicit selection. The search box narrows what is
 * DISPLAYED and nothing else.
 */
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Feature } from "shared/feature"
import { Sl2Btn } from "../Sl2Btn"
import FeatureBulkConfirmDialog from "./FeatureBulkConfirmDialog"
import FeatureRolloutRowItem from "./FeatureRolloutRow"
import FeatureRolloutSummary from "./FeatureRolloutSummary"
import {
  FeatureRolloutAction,
  FeatureRolloutBulkPlan,
  FeatureRolloutFilter,
  filterFeatureRolloutRows,
  planFeatureRolloutBulk,
} from "./featureRolloutModel"
import {
  FeatureWriteFailure,
  useFeatureAssignmentWrites,
} from "./useFeatureAssignmentWrites"
import { useFeatureRollout } from "./useFeatureRollout"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const headerCellSx = {
  fontSize: "0.6875rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  fontWeight: 600,
  color: "text.secondary",
}

type FeatureRolloutPanelProps = {
  feature: Feature
}

export const FeatureRolloutPanel: FC<FeatureRolloutPanelProps> = ({
  feature,
}) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState<FeatureRolloutFilter>("all")
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [plan, setPlan] = useState<FeatureRolloutBulkPlan | null>(null)

  const writes = useFeatureAssignmentWrites(feature)

  const {
    rows,
    counts,
    isLoading,
    error: loadError,
    tenantsQuery,
    assignmentsQuery,
  } = useFeatureRollout(feature)

  const shown = useMemo(
    () => filterFeatureRolloutRows(rows, search, filter),
    [rows, search, filter],
  )

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected],
  )

  const toggleSelect = useCallback((tenantId: string) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[tenantId]) delete next[tenantId]
      else next[tenantId] = true
      return next
    })
  }, [])

  /**
   * Select-all applies to the ROWS ON SCREEN, and does so by writing each id
   * into the selection — so narrowing the filter afterwards cannot silently
   * widen what a bulk action covers. The selection is always enumerable.
   */
  const allShownSelected =
    shown.length > 0 && shown.every((r) => selected[r.tenantId])

  const toggleSelectAll = useCallback(() => {
    if (allShownSelected) {
      setSelected((prev) => {
        const next = { ...prev }
        shown.forEach((r) => delete next[r.tenantId])
        return next
      })
      return
    }
    setSelected((prev) => {
      const next = { ...prev }
      shown.forEach((r) => {
        next[r.tenantId] = true
      })
      return next
    })
  }, [allShownSelected, shown])

  const openBulk = useCallback(
    (action: FeatureRolloutAction) =>
      setPlan(planFeatureRolloutBulk(rows, selectedIds, action)),
    [rows, selectedIds],
  )

  const confirmBulk = useCallback(async () => {
    if (!plan) return
    const ok = await writes.applyAction(plan.changing, plan.action)
    setPlan(null)
    // Keep a failed selection intact so the operator can retry the same set;
    // clear it on success so the next action starts from an empty scope.
    if (ok) setSelected({})
  }, [plan, writes])

  const onRetry = useCallback(
    (failure: FeatureWriteFailure) => writes.retry(failure, rows),
    [writes, rows],
  )

  const chips: Array<{ key: FeatureRolloutFilter; label: string; n: number }> =
    [
      { key: "all", label: t("feature.filterAll"), n: counts.total },
      { key: "on", label: t("feature.filterOn"), n: counts.granted },
      {
        key: "off",
        label: t("feature.filterOff"),
        n: counts.total - counts.granted,
      },
      {
        key: "unassigned",
        label: t("feature.filterNeverSet"),
        n: counts.unassigned,
      },
    ]

  return (
    <Stack data-ui="UI-SL2W-273" spacing={2.5} sx={{ width: "100%" }}>
      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
        <FeatureRolloutSummary counts={counts} />
      </Box>

      <Box sx={{ ...cardSx, overflow: "hidden" }}>
        {/* Filter bar */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            {t("feature.assignmentsByTenant")}
          </Typography>
          <TextField
            size="small"
            placeholder={t("feature.filterOrganizations") as string}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 260 }}
            inputProps={{ "data-testid": "feature-rollout-search" } as any}
          />
          <Stack direction="row" spacing={0.75}>
            {chips.map((c) => (
              <Chip
                key={c.key}
                size="small"
                label={`${c.label} ${c.n}`}
                onClick={() => setFilter(c.key)}
                data-testid={`feature-rollout-filter-${c.key}`}
                variant={filter === c.key ? "filled" : "outlined"}
                sx={
                  filter === c.key
                    ? { bgcolor: "#234285", color: "white", fontWeight: 500 }
                    : { fontWeight: 500 }
                }
              />
            ))}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          {writes.isBusy && <CircularProgress size={16} />}
          <Typography variant="caption" color="text.secondary">
            {t("feature.showingCount", {
              shown: shown.length,
              total: counts.total,
            })}
          </Typography>
        </Stack>

        {/* Bulk bar — present only when a selection exists (DoD5) */}
        {selectedIds.length > 0 && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            data-testid="feature-rollout-bulk-bar"
            sx={{
              px: 2.5,
              py: 1.25,
              bgcolor: "rgba(35, 66, 133, 0.06)",
              borderBottom: "1px solid",
              borderColor: "rgba(35, 66, 133, 0.15)",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "#234285" }}
            >
              {t("feature.nSelected", { count: selectedIds.length })}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Sl2Btn size="small" onClick={() => setSelected({})}>
              {t("feature.clearSelection")}
            </Sl2Btn>
            <Sl2Btn
              size="small"
              variant="outlined"
              onClick={() => openBulk("deactivate")}
              data-testid="feature-rollout-bulk-off"
            >
              {t("deactivate")}
            </Sl2Btn>
            <Sl2Btn
              size="small"
              variant="contained"
              onClick={() => openBulk("activate")}
              data-testid="feature-rollout-bulk-on"
              sx={{ bgcolor: "#234285" }}
            >
              {t("activate")}
            </Sl2Btn>
          </Stack>
        )}

        {writes.failureCount > 0 && (
          <Alert
            severity="error"
            sx={{ borderRadius: 0 }}
            data-testid="feature-rollout-write-error"
          >
            {t("feature.writesFailed", { count: writes.failureCount })}
          </Alert>
        )}

        {/* Column headers */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            bgcolor: "#fbfcfe",
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.06)",
          }}
        >
          <Checkbox
            size="small"
            checked={allShownSelected}
            indeterminate={
              !allShownSelected && shown.some((r) => selected[r.tenantId])
            }
            onChange={toggleSelectAll}
            disabled={shown.length === 0}
            inputProps={
              {
                "aria-label": t("feature.selectAllShown") as string,
                "data-testid": "feature-rollout-select-all",
              } as any
            }
            sx={{ p: 0.5 }}
          />
          <Typography sx={{ ...headerCellSx, flex: 1 }}>
            {t("tenant")}
          </Typography>
          <Typography sx={{ ...headerCellSx, width: 150, flex: "0 0 auto" }}>
            {t("feature.columnSource")}
          </Typography>
          <Typography sx={{ ...headerCellSx, width: 190, flex: "0 0 auto" }}>
            {t("feature.columnLastChange")}
          </Typography>
          <Typography
            sx={{
              ...headerCellSx,
              width: 150,
              flex: "0 0 auto",
              textAlign: "right",
            }}
          >
            {t("feature.columnState")}
          </Typography>
        </Stack>

        {isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">{t("processing")}</Typography>
          </Stack>
        )}

        {!!loadError && !isLoading && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {(tenantsQuery.error as any)?.message ||
              (assignmentsQuery.error as any)?.message ||
              t("feature.loadFailed")}
          </Alert>
        )}

        {!isLoading && !loadError && shown.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2 }}
            data-testid="feature-rollout-empty"
          >
            {t("noData")}
          </Typography>
        )}

        {!isLoading && shown.length > 0 && (
          <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
            {shown.map((row) => (
              <FeatureRolloutRowItem
                key={row.tenantId}
                row={row}
                selected={!!selected[row.tenantId]}
                onSelect={toggleSelect}
                onToggle={writes.toggleRow}
                isPending={writes.isPending(row.tenantId)}
                failure={writes.failureFor(row.tenantId)}
                onRetry={onRetry}
              />
            ))}
          </Box>
        )}
      </Box>

      <FeatureBulkConfirmDialog
        plan={plan}
        isBusy={writes.isBusy}
        onCancel={() => setPlan(null)}
        onConfirm={confirmBulk}
      />
    </Stack>
  )
}

export default FeatureRolloutPanel
